<template>
  <div>
    <v-row>
      <v-spacer />
      <Icon
      v-if="tract.tractGroupId != null"
      class="mr-5 mt-2"
      :small="false"
      icon="mdi-content-copy"
      elementId="tract-destination-copy"
      :tooltipText="copyTooltipText"
      :iconColor="copyDisabled ? 'grey' : 'primary'"
      :overrideClickable="copyDisabled"
      @icon-clicked="openCopyDialog"
      />
    </v-row>
    <v-container fluid v-if="tractDestinations.length > 0" id="tract-form-operational" class="mt-4">
      <v-row>
        <v-col cols="auto">
          <span class="title">
            {{$t('tractDestinations')}}
          </span>
        </v-col>

        <v-col cols="auto">
          <Icon
          :small="false"
          icon="mdi-plus"
          elementId="tract-destination-add"
          :tooltipText="$t('newTractDestination')"
          :disabled="!userAssignedClaim(UserClaims.ContractManager)"
          @icon-clicked="openTDForm(undefined)"
          />
        </v-col>
      </v-row>

      <v-row dense >
        <v-col
        v-for="tractDestination in tractDestinations"
        :key="tractDestination.tractDestinationId"
        cols="12" md="6" :lg="lgColsForOperationalCards" :xl="xlColsForOperationalCards">
          <DestinationCard
          :tractDestination="tractDestination"
          @edit-destination="openTDForm(tractDestination)"
          @delete-destination="confirmDeleteDestination(tractDestination)"
          @new-contract-pressed="openContractForm(tractDestination)"
          />
        </v-col>
      </v-row>
    </v-container>

    <v-container fluid v-else>
      <v-row align="center" justify="center" class="mt-6">
        <p class="title">
          {{$t('noTractDestinations')}}
        </p>
      </v-row>
      <v-row align="center" justify="center">
        <v-btn text color="primary" @click="openTDForm(undefined)" :disabled="!userAssignedClaim(UserClaims.ContractManager)">
          {{$t('addNew')}}
        </v-btn>
      </v-row>
    </v-container>

    <v-divider class="my-6" />

    <v-container fluid class="mt-4" v-if="tractInstructions !== null">
      <v-row dense>
        <v-col cols="12" md="6" :lg="lgColsForOperationalCards" :xl="xlColsForOperationalCards">
          <TractInstructionsCard
            :tractInstructions="tractInstructions"
            @edit-instructions="openInstructionsForm"
            @delete-instructions="confirmDeleteInstructions"
          />
        </v-col>
      </v-row>
    </v-container>

    <v-container fluid v-else>
      <v-row align="center" justify="center" class="mt-6">
        <p class="title">
          {{$t('noTractInstructions')}}
        </p>
      </v-row>
      <v-row align="center" justify="center">
        <v-btn text color="primary" @click="openInstructionsForm" :disabled="!userAssignedClaim(UserClaims.ContractManager)">
          {{$t('addNew')}}
        </v-btn>
      </v-row>
    </v-container>

    <Dialog
    :stateId="dialogId"
    hideRefresh
    @close="resetDialogs">
      <TractDestinationForm
        v-if="activeDialog === 'tractDestination'"
        :propTractDestination="focusedTractDestination"
        :allTractDestinations="tractDestinations"
        :tract="tract"
        @close="closeForm(true)"
        @create-contract="createContractFromDestination"
      />

      <TractContractForm
        v-if="activeDialog === 'newContract' && focusedTractDestination"
        :tract="tract"
        :savedTract="savedTract"
        :tractDestination="focusedTractDestination"
        :landowners="tractGroup?.landowners ?? landowners"
        @close="closeForm(true)"
      />

      <TractInstructionsForm
        v-if="activeDialog === 'tractInstructions'"
        :propTractInstructions="tractInstructions"
        :tractId="tractId"
        @close="closeInstructionsForm"
      />

      <CopyFromTractDialog
        v-if="activeDialog === 'copyFromTract'"
        :tracts="otherTractsInGroup"
        :loading="copying"
        @confirm="copyFromTract"
        @cancel="closeDialog"
      />

      <ConfirmDialog
        v-if="activeDialog === 'confirmDeleteDestination' && focusedTractDestination"
        :title="$t('deleteTractDestination', { name: focusedTractDestination.destinationAccountName })"
        :body="$t('deleteTractDestinationBody')"
        @confirm="deleteConfirmed"
        @cancel="closeDialog"
      />

      <ConfirmDialog
        v-if="activeDialog === 'confirmDeleteInstructions'"
        :title="$t('deleteTractInstructions')"
        :body="$t('deleteTractInstructionsBody')"
        @confirm="deleteInstructionsConfirmed"
        @cancel="closeDialog"
      />
    </Dialog>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { UserClaims, TractTypeCategory } from '@/utils/Enumerations.js'
import { userAssignedClaim } from '@/utils/ClaimUtility.js'
export default {
  name: 'Operational',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    TractDestinationForm: () => import('@/components/tract/tract-form/TractDestinationForm.vue'),
    DestinationCard: () => import('@/components/tract/tract-form/DestinationCard'),
    TractContractForm: () => import('@/components/tract/tract-form/TractContractForm.vue'),
    ConfirmDialog: () => import('@/components/helper/ConfirmDialog.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    TractInstructionsCard: () => import('@/components/tract/tract-form/TractInstructionsCard.vue'),
    TractInstructionsForm: () => import('@/components/tract/tract-form/TractInstructionsForm.vue'),
    CopyFromTractDialog: () => import('@/components/tract/CopyFromTractDialog.vue')
  },

  props: {
    tract: {
      type: Object,
      required: true
    },
    savedTract: {
      type: Object,
      default: null
    },
    landowners: {
      type: Array,
      default: () => []
    },
    refreshTractDestinations: {
      type: Boolean,
      default: true
    }
  },

  data: () => ({
    loading: false,
    activeDialog: null,
    dialogId: 'tract-operational-dialog',
    tractDestinations: [],
    focusedTractDestination: undefined,
    UserClaims,
    tractGroup: undefined,
    tractInstructions: null,
    copying: false
  }),

  computed: {
    tractId () {
      return this.tract.tractId
    },

    productIds () {
      if (this.tract.type.category === TractTypeCategory.Stumpage.value) {
        return this.tract.tractContents.map(cd => cd.productId)
      }
      return null
    },

    lgColsForOperationalCards () {
      return this.tractDestinations.some(td => td.products.some(p => p.scaleFrequency > 0)) ? 6 : 4
    },

    xlColsForOperationalCards () {
      return this.tractDestinations.some(td => td.products.some(p => p.scaleFrequency > 0)) ? 4 : undefined
    },

    harvestStartDateNotEntered () {
      return this.tract.harvestStartDate === null || this.tract.harvestStartDate === undefined
    },

    otherTractsInGroup () {
      if (!this.tractGroup?.tracts) return []
      return this.tractGroup.tracts.filter(t => t.tractId !== this.tractId)
    },

    copyDisabled () {
      return !userAssignedClaim(UserClaims.ContractManager) || this.tractDestinations.length > 0 || this.tractInstructions !== null
    },

    copyTooltipText () {
      if (!userAssignedClaim(UserClaims.ContractManager)) return this.$t('copyDisabledNoPermission')
      if (this.tractDestinations.length > 0) return this.$t('copyDisabledDestinationsExist')
      if (this.tractInstructions !== null) return this.$t('copyDisabledInstructionsExist')
      return this.$t('copyFromAnotherTract')
    }
  },

  watch: {
    refreshTractDestinations (val) {
      if (val) {
        this.refresh(this.tractId)
      }
    }
  },

  async created () {
    await this.refresh(this.tractId)
    this.tractInstructions = this.tract.tractInstructions ?? null
    if (this.tract.tractGroupId != null) {
      this.tractGroup = await this.fetchTractGroup(this.tract.tractGroupId)
    }
  },

  methods: {
    ...mapActions('tract-destination', ['fetchTractDestinations', 'postTractDestination', 'deleteTractDestination']),
    ...mapActions('tract', ['fetchTractGroup']),
    ...mapActions('tract', ['fetchContractsForTract']),
    ...mapActions('tract-instructions', ['fetchTractInstructions', 'createTractInstructions', 'deleteTractInstructions']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    userAssignedClaim,

    resetDialogs () {
      this.activeDialog = null
      this.focusedTractDestination = undefined
    },

    openDialog (dialogKey, options = {}, focusedItem = undefined) {
      this.resetDialogs()
      if (focusedItem !== undefined) {
        this.focusedTractDestination = focusedItem
      }
      this.activeDialog = dialogKey
      this.openOrUpdateDialog({ id: this.dialogId, ...options })
    },

    closeDialog (onClose = null) {
      this.resetDialogs()
      this.closeDialogsAtOrAbove(this.dialogId)
      if (onClose) onClose()
    },

    async refresh (tractId) {
      this.loading = true
      try {
        this.tractDestinations = await this.fetchTractDestinations(tractId)
      } finally {
        this.loading = false
        this.$emit('tract-destinations-refreshed')
      }
    },

    openTDForm (tractDestination) {
      if (this.harvestStartDateNotEntered) {
        this.setSnackError(this.$t('harvestStartDateNotSpecifiedDest'))
        return
      }
      this.openDialog('tractDestination', { width: '50vw' }, tractDestination)
    },

    confirmDeleteDestination (tractDestination) {
      if (tractDestination.contractCount > 0) {
        this.setSnackError(this.$t('cannotDeleteDestination'))
        return
      }
      this.openDialog('confirmDeleteDestination', { width: '400px', allowFullscreen: false }, tractDestination)
    },

    async deleteConfirmed () {
      await this.deleteTractDestination({ tractId: this.tractId, tractDestinationId: this.focusedTractDestination.tractDestinationId })
      this.closeDialog(() => this.refresh(this.tractId))
    },

    closeForm (refresh) {
      this.closeDialog(refresh ? () => {
        this.refresh(this.tractId)
        this.$emit('refresh-tract-contracts')
      } : null)
    },

    openContractForm (tractDestination) {
      if (this.tract.type.category === 0 && (this.tract.loggers.length === 0 || this.tract.loggers.length === undefined)) {
        this.setSnackError(this.$t('mustSpecifyLoggersOnTract'))
        return
      }

      if (this.harvestStartDateNotEntered) {
        this.setSnackError(this.$t('harvestStartDateNotSpecified'))
        return
      }

      if (!(tractDestination.products.length > 0)) {
        this.setSnackError(this.$t('destinationMustHaveProducts'))
        return
      }

      this.openDialog('newContract', { fullscreen: true }, tractDestination)
    },

    createContractFromDestination (tractDestination) {
      this.closeForm(true)
      this.$nextTick(() => {
        this.openContractForm(tractDestination)
      })
    },

    openInstructionsForm () {
      this.openDialog('tractInstructions', { width: '50vw' })
    },

    openCopyDialog () {
      if (this.copyDisabled) return
      this.openDialog('copyFromTract', { width: '500px' })
    },

    confirmDeleteInstructions () {
      this.openDialog('confirmDeleteInstructions', { width: '400px', allowFullscreen: false })
    },

    async deleteInstructionsConfirmed () {
      await this.deleteTractInstructions(this.tractId)
      this.closeDialog(() => { this.tractInstructions = null })
    },

    closeInstructionsForm (result) {
      this.closeDialog(result ? () => { this.tractInstructions = result } : null)
    },

    async copyFromTract (sourceTract) {
      this.copying = true
      try {
        const sourceDestinations = await this.fetchTractDestinations(sourceTract.tractId)
        for (const dest of sourceDestinations) {
          await this.postTractDestination({
            tractId: this.tractId,
            destinationAccountId: dest.destinationAccountId,
            distance: dest.distance,
            requiresExt2: dest.requiresExt2,
            yardInstructionId: dest.yardInstructionId,
            products: dest.products.map(p => ({
              productId: p.productId,
              scaleFrequency: p.scaleFrequency
            }))
          })
        }

        const sourceInstructions = await this.fetchTractInstructions(sourceTract.tractId)
        if (sourceInstructions.completedTicketPrintCount !== null && sourceInstructions.kioskInstructions !== null) {
          const result = await this.createTractInstructions({
            tractId: this.tractId,
            instructions: {
              kioskInstructions: sourceInstructions.kioskInstructions,
              completedTicketPrintCount: sourceInstructions.completedTicketPrintCount
            }
          })
          this.tractInstructions = result
        }

        this.setSnack(this.$t('copySuccess', { name: sourceTract.name }))
        this.closeDialog()
        await this.refresh(this.tractId)
      } finally {
        this.copying = false
      }
    }
  }
}
</script>
